import { EmptyState, InlineMessage } from '@crowdex/ui/components/feedback';
import { SquareTerminal } from 'lucide-react';
import { Component, type ReactNode } from 'react';
import { AppViewPanel } from '@/features/apps/app-view-panel';
import { RemoteAppDocked } from '@/features/apps/remote-app-views';
import { AssistantPanel } from '@/features/assistants/assistant-panel';
import { LivePanel } from '@/features/voice-call/live-panel';
import { openAppHome } from '@/lib/app-home';
import type { NativePluginContext } from './native-plugin';
import { terminalSurface, webSurface } from './platform-services';
import { PlatformTerminalSurface } from './platform-terminal-surface';
import { PlatformWebSurface } from './platform-web-surface';
import type { PluginPanel } from './plugin-workbench';

/** Host surfaces and app views share this mount point. */
export function usesNativePluginPanel(panel: PluginPanel): boolean {
  return Boolean(panel.hostView || panel.appView || panel.remoteView);
}

export class PluginBoundary extends Component<{ children: ReactNode }, { error: string | null }> {
  override state: { error: string | null } = { error: null };
  static getDerivedStateFromError(error: unknown) {
    return { error: error instanceof Error ? error.message : String(error) };
  }
  override render() {
    return this.state.error ? (
      <div className="p-4">
        <InlineMessage title="The plugin stopped working." tone="error">
          {this.state.error}
        </InlineMessage>
      </div>
    ) : (
      this.props.children
    );
  }
}

/**
 * No terminal to show (M16): why, and what gets one — a repository to open it in, or the
 * computer or sandbox the conversation runs on.
 */
export function TerminalUnavailable({ workspace }: { workspace: string | null }) {
  return (
    <EmptyState
      className="min-h-72 justify-center"
      description={
        workspace === null
          ? 'A terminal opens in the conversation’s repository. Choose one in Where it runs, then open Terminal again.'
          : 'The terminal runs on the computer or cloud sandbox the conversation works on. It opens here once that is connected: open Crowdex on that computer, or run the conversation in a cloud sandbox.'
      }
      icon={<SquareTerminal />}
      title={workspace === null ? 'No repository for a terminal' : 'No terminal on this device'}
    />
  );
}

export function NativePluginPanel({
  panel,
  tabId,
  current = true,
  onTitleChange,
  context,
}: {
  panel: PluginPanel;
  /** The tab an app's view shows in (an app has one per item shown). */
  tabId?: string | undefined;
  /** Of an app's tabs shown together, the one shown last: the agent's context. */
  current?: boolean | undefined;
  /** The title of what an app's tab shows, which names it. */
  onTitleChange?: ((title: string | null) => void) | undefined;
  context: NativePluginContext;
}) {
  const scopeKey = [
    panel.version,
    context.taskId,
    context.workspace ?? '',
    context.cloudRuntime === null ? 'local' : `cloud:${context.cloudRuntime.id}`,
  ].join(':');
  if (panel.hostView === 'terminal-surface') {
    const surface = context.services.get(terminalSurface);
    return surface ? (
      <PlatformTerminalSurface {...surface} />
    ) : (
      <TerminalUnavailable workspace={context.workspace ?? null} />
    );
  }
  if (panel.hostView === 'live') return <LivePanel />;
  if (panel.hostView === 'project-tasks' || panel.hostView === 'project')
    return (
      <PluginBoundary>
        <AssistantPanel conversationId={context.taskId} view={panel.hostView} />
      </PluginBoundary>
    );
  if (panel.hostView === 'web-surface') {
    const surface = context.services.get(webSurface);
    return surface ? (
      <PlatformWebSurface {...surface} visible={surface.visible} />
    ) : (
      <div className="p-4 text-muted-foreground text-sm">
        The browser is not available in this session.
      </div>
    );
  }
  // An app's view shows in a tab of its own, one per item shown.
  if (panel.appView && tabId !== undefined) {
    const appView = panel.appView;
    return (
      <PluginBoundary key={scopeKey}>
        <AppViewPanel
          context={context}
          current={current}
          onTitleChange={onTitleChange}
          panel={{ ...panel, appView }}
          tabId={tabId}
        />
      </PluginBoundary>
    );
  }
  if (panel.remoteView)
    return (
      <PluginBoundary key={scopeKey}>
        {/* An embedded web app has the host's frame too (its own top bar and column are not
            shown): its title, actions and sections come through the embed bridge. */}
        <RemoteAppDocked
          openElsewhere={{
            label: 'Open full screen',
            onSelect: () => openAppHome(panel.plugin),
          }}
          panel={panel}
          visible={context.visible ?? true}
        />
      </PluginBoundary>
    );
  return (
    <div className="p-4">
      <InlineMessage title="This plugin view is unavailable." tone="error">
        {panel.plugin}/{panel.id}
      </InlineMessage>
    </div>
  );
}
